import React from "react";
import { FiCalendar, FiChevronRight } from "react-icons/fi";
import { SOCIAL_EVENT_TYPES, SOCIAL_MVP_LABELS, SOCIAL_TOUR_SCHEMES } from '../../constants/config';
import { getSocialPalette, resolveImage } from '../../utilities/infographics';
import BackgroundPicker from './BackgroundPicker';
import ColorsControl from './ColorsControl';
import '../../styles/infographics.css';

// Реестр контролов инфографики.
// Сервер в дескрипторе шаблона присылает available_settings — массив ИМЁН,
// которые в приложении вызываются как this[item]() (CreateSocialPost.renderParams).
// Здесь те же имена сопоставлены React-компонентам, поэтому новый шаблон
// с сервера подхватывается без правок фронта — ровно как в приложении.
//
// Каждый контрол получает:
//   value    — весь объект параметров
//   setValue — (ключ, значение) с перерисовкой превью
//   pattern  — дескриптор шаблона
//   ctx      — справочники и обработчики (лиги, туры, открытие модалок)

const Row = ({ label, hint, children }) => (
    <div className="igControl">
        <div className="igControlHead">
            <span className="igControlLabel">{label}</span>
            {hint ? <span className="igControlHint">{hint}</span> : null}
        </div>
        {children}
    </div>
);

const PickerButton = ({ children, onClick, placeholder }) => (
    <button type="button" className="igPicker" onClick={onClick}>
        <span className={children ? 'igPickerValue' : 'igPickerPlaceholder'}>
            {children || placeholder}
        </span>
        <FiChevronRight />
    </button>
);

const Toggle = ({ label, hint, checked, onChange }) => (
    <div className="igControl igControlToggle">
        <div className="igControlHead">
            <span className="igControlLabel">{label}</span>
            {hint ? <span className="igControlHint">{hint}</span> : null}
        </div>
        <label className="igSwitch">
            <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
            <span className="igSwitchSlider" />
        </label>
    </div>
);

// ---------- Лига и сезон ----------

const ChooseLeague = ({ value, ctx }) => {
    const league = value.activeLeague;
    return (
        <Row label="Лига">
            <PickerButton onClick={ctx.openLeaguePicker} placeholder="Выберите лигу">
                {league ? (
                    <span className="igPickerWithLogo">
                        {resolveImage(league.league_image) ? (
                            <img src={resolveImage(league.league_image)} alt="" />
                        ) : null}
                        {league.league_name}
                    </span>
                ) : null}
            </PickerButton>
        </Row>
    );
};

const SeasonChoose = ({ value, setValue }) => {
    const league = value.activeLeague;
    const seasons = league && league.seasons ? league.seasons : [];
    if (!league) {
        return (
            <Row label="Сезон">
                <p className="igControlNote">Сначала выберите лигу.</p>
            </Row>
        );
    }
    return (
        <Row label="Сезон">
            <select
                className="igSelect"
                value={value.seasonIndex}
                onChange={(e) => setValue('seasonIndex', Number(e.target.value))}
            >
                {seasons.map((season, index) => (
                    <option key={season.season_id} value={index}>
                        {season.season_name || season.name}
                    </option>
                ))}
            </select>
        </Row>
    );
};

const ChooseStage = ({ value, ctx }) => {
    const stage = value.activeStage;
    return (
        <Row label="Тур">
            <PickerButton onClick={ctx.openStagePicker} placeholder="Выберите тур">
                {stage ? stage.name : null}
            </PickerButton>
        </Row>
    );
};

const ChooseDate = ({ value, setValue }) => (
    <Row label="Дата" hint="По ней подбираются матчи">
        <div className="igDateRow">
            <FiCalendar />
            <input
                type="date"
                className="igInput"
                value={value.date || ''}
                onChange={(e) => setValue('date', e.target.value)}
            />
        </div>
    </Row>
);

// ---------- Оформление ----------

const ActiveColor = ({ value, setValue, pattern }) => {
    const palette = getSocialPalette(pattern.id);
    return (
        <Row label="Цвет" hint="Основной цвет шаблона">
            <div className="igSwatches">
                {palette.map((color, index) => (
                    <button
                        type="button"
                        key={color + index}
                        aria-label={color}
                        className={`igSwatch ${value.activeColor === index + 1 ? 'igSwatchActive' : ''}`}
                        style={{ background: color }}
                        onClick={() => setValue('activeColor', index + 1)}
                    />
                ))}
            </div>
        </Row>
    );
};

const SegmentedControl = ({ label, options, current, onPick }) => (
    <Row label={label}>
        <div className="igSegmented">
            {options.map((option, index) => (
                <button
                    type="button"
                    key={option}
                    className={`igSegmentedItem ${current === index + 1 ? 'igSegmentedItemActive' : ''}`}
                    onClick={() => onPick(index + 1)}
                >
                    {option}
                </button>
            ))}
        </div>
    </Row>
);

const ActiveHeaderEvo = ({ value, setValue }) => (
    <SegmentedControl
        label="Заголовок"
        options={['Результаты', 'Расписание']}
        current={value.activeHeader}
        onPick={(next) => setValue('activeHeader', next)}
    />
);

const ActiveBottomEvo = ({ value, setValue }) => (
    <SegmentedControl
        label="Соцсеть в подвале"
        options={['Ростов', 'Краснодар']}
        current={value.activeBottom}
        onPick={(next) => setValue('activeBottom', next)}
    />
);

// ---------- Выпадающие справочники ----------

const EventTypeChoose = ({ value, setValue }) => (
    <Row label="Тип статистики">
        <select
            className="igSelect"
            value={value.eventIndex}
            onChange={(e) => setValue('eventIndex', Number(e.target.value))}
        >
            {SOCIAL_EVENT_TYPES.map((item, index) => (
                <option key={item.value} value={index}>{item.label}</option>
            ))}
        </select>
    </Row>
);

const MvpLabelChoose = ({ value, setValue }) => (
    <Row label="Подпись">
        <select
            className="igSelect"
            value={value.mvpIndex}
            onChange={(e) => setValue('mvpIndex', Number(e.target.value))}
        >
            {SOCIAL_MVP_LABELS.map((item, index) => (
                <option key={item.value} value={index}>{item.label}</option>
            ))}
        </select>
    </Row>
);

// ---------- Переключатели ----------

const SwitchSponsors = ({ value, setValue }) => (
    <Toggle
        label="Спонсоры"
        hint="Полоса логотипов партнёров"
        checked={value.sponsorEnabled}
        onChange={(next) => setValue('sponsorEnabled', next)}
    />
);

const VideoEnabled = ({ value, setValue }) => (
    <Toggle
        label="Трансляция"
        checked={value.videoEnabled}
        onChange={(next) => setValue('videoEnabled', next)}
    />
);

const GoalTimeEnabled = ({ value, setValue }) => (
    <Toggle
        label="Время событий"
        hint="Минуты голов и карточек"
        checked={value.goalTimeEnabled}
        onChange={(next) => setValue('goalTimeEnabled', next)}
    />
);

const IsGroupEvents = ({ value, setValue }) => (
    <Toggle
        label="Группировка событий"
        checked={value.isGroupEvent}
        onChange={(next) => setValue('isGroupEvent', next)}
    />
);

const IncludeTourTeam = ({ value, setValue }) => (
    <Toggle
        label="Показать сборную в приложении"
        checked={value.includeTourTeam}
        onChange={(next) => setValue('includeTourTeam', next)}
    />
);

// ---------- Текстовые поля ----------

const AddLabel = ({ value, setValue }) => (
    <Row label="Хэштег">
        <input
            type="text"
            className="igInput"
            value={value.label || ''}
            placeholder="#мойтурнир"
            onChange={(e) => setValue('label', e.target.value)}
        />
    </Row>
);

const Socials = ({ value, setValue }) => (
    <Row label="Соцсети" hint="Подписи внизу картинки">
        <input
            type="text"
            className="igInput"
            value={value.social_vk || ''}
            placeholder="ВКонтакте"
            onChange={(e) => setValue('social_vk', e.target.value)}
        />
        <input
            type="text"
            className="igInput igInputSpaced"
            value={value.social_inst || ''}
            placeholder="Instagram"
            onChange={(e) => setValue('social_inst', e.target.value)}
        />
    </Row>
);

// ---------- Пикеры сущностей ----------
// Списки грузит и показывает редактор — контрол только открывает нужную модалку
// и отвечает за то, как выбранное выглядит в панели параметров.

const matchTitle = (match) => (
    `${match.home_team_name || 'Команда'} — ${match.guest_team_name || 'Команда'}`
);

const MatchChoose = ({ value, ctx }) => (
    <Row label="Матч">
        <PickerButton onClick={() => ctx.openPicker('match')} placeholder="Выберите матч">
            {value.activeMatch ? matchTitle(value.activeMatch) : null}
        </PickerButton>
    </Row>
);

const MatchesChoose = ({ value, ctx }) => {
    const chosen = Array.isArray(value.activeMatches) ? value.activeMatches : [];
    return (
        <Row label="Матчи" hint="Можно выбрать несколько">
            <PickerButton onClick={() => ctx.openPicker('matches')} placeholder="Выберите матчи">
                {chosen.length ? `Выбрано матчей: ${chosen.length}` : null}
            </PickerButton>
        </Row>
    );
};

const PlayerChoose = ({ value, ctx }) => {
    const player = value.activePlayer;
    return (
        <Row label="Игрок">
            <PickerButton onClick={() => ctx.openPicker('player')} placeholder="Выберите игрока">
                {player ? `${player.family || ''} ${player.name || ''}`.trim() : null}
            </PickerButton>
        </Row>
    );
};

const GroupChoose = ({ value, ctx }) => (
    <Row label="Группа">
        <PickerButton onClick={() => ctx.openPicker('group')} placeholder="Выберите группу">
            {value.activeGroups && !Array.isArray(value.activeGroups)
                ? `Группа ${value.activeGroups.name}` : null}
        </PickerButton>
    </Row>
);

const GroupChooseMultiple = ({ value, ctx }) => {
    const chosen = Array.isArray(value.activeGroups) ? value.activeGroups : [];
    return (
        <Row label="Группы" hint="Можно выбрать несколько">
            <PickerButton onClick={() => ctx.openPicker('groups')} placeholder="Выберите группы">
                {chosen.length ? `Выбрано групп: ${chosen.length}` : null}
            </PickerButton>
        </Row>
    );
};

const playerNames = (list) => (
    (Array.isArray(list) ? list : [])
        .map(one => `${one.family || ''} ${one.name || ''}`.trim())
        .filter(Boolean)
);

const MainTeamChoose = ({ value, ctx }) => {
    const chosen = playerNames(value.activeMainTeam);
    return (
        <Row label="Состав первой команды" hint="Игроки хозяев на картинке">
            <PickerButton onClick={() => ctx.openPicker('mainTeam')} placeholder="Выберите игроков">
                {chosen.length ? chosen.join(', ') : null}
            </PickerButton>
        </Row>
    );
};

const SubTeamChoose = ({ value, ctx }) => {
    const chosen = playerNames(value.activeSubTeam);
    return (
        <Row label="Состав второй команды" hint="Игроки гостей на картинке">
            <PickerButton onClick={() => ctx.openPicker('subTeam')} placeholder="Выберите игроков">
                {chosen.length ? chosen.join(', ') : null}
            </PickerButton>
        </Row>
    );
};

const CoachesChoose = ({ value, ctx }) => {
    const chosen = playerNames(value.activeCoaches);
    return (
        <Row label="Тренеры">
            <PickerButton onClick={() => ctx.openPicker('coaches')} placeholder="Выберите тренеров">
                {chosen.length ? chosen.join(', ') : null}
            </PickerButton>
        </Row>
    );
};

const TourTeamChoose = ({ value, ctx }) => {
    const chosen = playerNames(value.activePlayers);
    return (
        <Row label="Сборная тура" hint="Игроки по позициям выбранной схемы">
            <PickerButton onClick={() => ctx.openPicker('tourTeam')} placeholder="Соберите состав">
                {chosen.length ? `Выбрано игроков: ${chosen.length}` : null}
            </PickerButton>
        </Row>
    );
};

const SchemeChoose = ({ value, setValue }) => {
    const current = value.activeScheme;
    return (
        <Row label="Схема расстановки">
            <select
                className="igSelect"
                value={current === false || current === undefined ? '' : String(current)}
                onChange={(e) => setValue(
                    'activeScheme',
                    e.target.value === '' ? false : Number(e.target.value),
                )}
            >
                <option value="">— не выбрана —</option>
                {SOCIAL_TOUR_SCHEMES.map(group => (
                    <optgroup key={group.id} label={group.name}>
                        {group.data.map(scheme => (
                            <option key={scheme.id} value={scheme.id}>{scheme.name}</option>
                        ))}
                    </optgroup>
                ))}
            </select>
        </Row>
    );
};

// Игроки, которые числятся сразу в нескольких командах: сервер возвращает их
// в поле players_with_several_teams, а админ выбирает, чей логотип рисовать
const PlayerTeamChange = ({ value, setValue }) => {
    const rows = Array.isArray(value.playersTeamImages) ? value.playersTeamImages : [];
    if (rows.length === 0) {
        return (
            <Row label="Команда игрока на картинке">
                <p className="igControlNote">
                    Все выбранные игроки числятся в одной команде — выбирать нечего.
                </p>
            </Row>
        );
    }
    const pickTeam = (playerIndex, teamId) => {
        const next = rows.map((row, index) => (
            index === playerIndex ? { ...row, team_id: teamId } : row
        ));
        setValue('playersTeamImages', next);
    };
    return (
        <Row label="Команда игрока на картинке" hint="У этих игроков несколько команд">
            {rows.map((row, index) => (
                <div className="igColorSlot" key={row.player_id || index}>
                    <span className="igColorSlotLabel">
                        {`${row.family || ''} ${row.name || ''}`.trim() || 'Игрок'}
                    </span>
                    <select
                        className="igSelect"
                        value={row.team_id || ''}
                        onChange={(e) => pickTeam(index, Number(e.target.value))}
                    >
                        {(row.teams || []).map(team => (
                            <option key={team.team_id || team.id} value={team.team_id || team.id}>
                                {team.name || team.team_name}
                            </option>
                        ))}
                    </select>
                </div>
            ))}
        </Row>
    );
};

const PlayoffChoose = ({ value, ctx }) => {
    const rounds = Array.isArray(value.activePlayoffMatches) ? value.activePlayoffMatches : [];
    const filled = rounds.reduce((sum, round) => (
        sum + (round.data || []).filter(slot => slot.id).length
    ), 0);
    return (
        <Row label="Сетка плей-офф" hint="Матчи по раундам">
            <PickerButton onClick={() => ctx.openPicker('playoff')} placeholder="Соберите сетку">
                {filled ? `Заполнено матчей: ${filled}` : null}
            </PickerButton>
        </Row>
    );
};

// ---------- Свои фоны ----------
// Пять независимых слотов — столько же, сколько в приложении

const makeBackgroundControl = (stateKey, label) => {
    const Control = ({ value, setValue, pattern }) => (
        <BackgroundPicker
            label={label}
            pattern={pattern}
            value={value[stateKey]}
            onChange={(next) => setValue(stateKey, next)}
        />
    );
    return Control;
};

// ---------- Реестр ----------

// Имена совпадают с методами CreateSocialPost в приложении.
export const CONTROL_REGISTRY = {
    renderChooseLeague: ChooseLeague,
    renderSeasonChoose: SeasonChoose,
    renderChooseStage: ChooseStage,
    renderChooseDate: ChooseDate,
    renderActiveColor: ActiveColor,
    renderActiveHeaderEvo: ActiveHeaderEvo,
    renderActiveBottomEvo: ActiveBottomEvo,
    renderEventTypeChoose: EventTypeChoose,
    renderMvpLabelChoose: MvpLabelChoose,
    renderSwitchSponsors: SwitchSponsors,
    renderVideoEnabled: VideoEnabled,
    renderGoalTimeEnabled: GoalTimeEnabled,
    renderIsGroupEvents: IsGroupEvents,
    renderIncludeTourTeam: IncludeTourTeam,
    renderAddLabel: AddLabel,
    renderSocials: Socials,
    renderMatchChoose: MatchChoose,
    renderMatchesChoose: MatchesChoose,
    renderPlayerChoose: PlayerChoose,
    renderLeagueChoose: GroupChoose,
    renderLeagueChooseMultiple: GroupChooseMultiple,
    renderCustomBackground: makeBackgroundControl('customBg', 'Свой фон'),
    renderCustomBackgroundTwo: makeBackgroundControl('customBgTwo', 'Свой фон 2'),
    renderCustomBackgroundThree: makeBackgroundControl('customBgThree', 'Свой фон 3'),
    renderCustomBackgroundFour: makeBackgroundControl('customBgFour', 'Свой фон 4'),
    renderCustomBackgroundFive: makeBackgroundControl('customBgFive', 'Свой фон 5'),
    renderColorsChoose: ColorsControl,
    renderSchemeChoose: SchemeChoose,
    renderMainTeamChoose: MainTeamChoose,
    renderSubTeamChoose: SubTeamChoose,
    renderCoachesChoose: CoachesChoose,
    renderTeamChoose: TourTeamChoose,
    renderPlayerTeamChange: PlayerTeamChange,
    renderPlayoffChoose: PlayoffChoose,
};

// Контролы, которые ещё не перенесены (пикеры матчей, игроков, схем и фонов).
// В приложении неизвестный ключ молча пропускается — на сайте показываем
// заглушку, иначе шаблон выглядит настроенным, а часть параметров потеряна.
export const PENDING_CONTROL_LABELS = {};
